import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { Edit3, Flame, Dumbbell, Activity, Target, Wrench, Clock, AlertCircle, ChevronRight, LayoutGrid, Check, CalendarDays, LogOut, LogIn, Cloud, CloudOff, Shield, KeyRound, Eye, EyeOff, ShieldCheck, SlidersHorizontal, ArrowRight, BookOpen, Shirt, Copy } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Popover, PopoverTrigger, PopoverContent } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { useProfile } from '../context/ProfileContext';
import { useAuth } from '../context/AuthContext';
import { useTierState } from '../hooks/useTierState';
import { useFeatureFlag } from '../context/FeatureFlagsContext';
import axios from 'axios';
import { weightUnit, displayWeight } from '../utils/units';
import { equipmentCategories, injuryRegions, injurySystemic, goalOptions, experienceLevels } from '../data/equipment';
import { recoveryActivities } from '../data/endurance';
import { programs, localISO } from '../data/programs';
import { PROSTHESIS_VARIANTS } from '../data/prosthesisVariants';
import { ADAPTIVE_SUBMODES } from '../data/adaptiveSubmodes';
import EditProfileModal from '../components/EditProfileModal';
import ProfileCompletenessMeter from '../components/ProfileCompletenessMeter';
import HealthPermissionCard from '../components/HealthPermissionCard';
import ResidualLimbLogCard from '../components/ResidualLimbLogCard';
import ConsentCenter from '../components/ConsentCenter';
import SubscriptionBlock from '../components/SubscriptionBlock';

const Section = ({ icon: Icon, iconFlipX = false, title, onEdit, editSection, children }) => (
  <div className="bg-enf-surface-deep border border-enf-surface-border rounded-xl p-6">
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2">
        <Icon className={`w-4 h-4 text-enf-accent-fg ${iconFlipX ? 'scale-x-[-1]' : ''}`} />
        <h3 className="text-sm font-semibold uppercase tracking-widest text-enf-text-secondary">{title}</h3>
      </div>
      {onEdit && (
        <button
          type="button"
          onClick={() => onEdit(editSection)}
          className="text-xs text-enf-text-muted hover:text-enf-accent-fg flex items-center gap-1"
          data-testid={`profile-edit-${editSection}`}
        >
          <Edit3 className="w-3 h-3" /> Edit
        </button>
      )}
    </div>
    {children}
  </div>
);

const Profile = () => {
  const { profile, reset, resetTrainingHistory, update, history, training } = useProfile();
  const { reason: tierReason } = useTierState();
  // Coupon perk: paid customers + lifetime grants + admin only.
  // Trial users (`app_trial`) and comp users (`comp`) intentionally excluded
  // — neither paid money for access, and the discount is a thank-you for
  // actual paid relationships. See session note.
  const COUPON_REASONS = ['subscription', 'subscription_grace', 'founder', 'hero', 'admin'];
  const { enabled: apparelCouponEnabled } = useFeatureFlag('apparel_coupon_card');
  const { enabled: phantomLimbEnabled } = useFeatureFlag('phantom_limb_tracker');
  const showApparelCoupon = COUPON_REASONS.includes(tierReason) && apparelCouponEnabled;
  const [couponCopied, setCouponCopied] = useState(false);
  const { user, logout } = useAuth();
  const [pwOpen, setPwOpen] = useState(false);
  const [pwCurrent, setPwCurrent] = useState('');
  const [pwNew, setPwNew] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [pwShow, setPwShow] = useState(false);
  const [pwBusy, setPwBusy] = useState(false);
  const [pwMsg, setPwMsg] = useState({ kind: '', text: '' }); // kind: '' | 'ok' | 'err'
  const [verifyState, setVerifyState] = useState('idle'); // 'idle' | 'sending' | 'sent' | 'error'
  const [verifyError, setVerifyError] = useState('');
  const [resetHistoryState, setResetHistoryState] = useState({ open: false, busy: false, result: null });
  const [exportBusy, setExportBusy] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const sendVerification = async () => {
    setVerifyState('sending');
    setVerifyError('');
    try {
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      await axios.post(`${API}/auth/send-verification`);
      setVerifyState('sent');
    } catch (err) {
      setVerifyError(err.response?.data?.detail || 'Could not send verification email. Try again in a moment.');
      setVerifyState('error');
    }
  };

  const submitPasswordChange = async (e) => {
    e.preventDefault();
    setPwMsg({ kind: '', text: '' });
    if (pwNew.length < 8) {
      setPwMsg({ kind: 'err', text: 'New password must be at least 8 characters.' });
      return;
    }
    if (pwNew !== pwConfirm) {
      setPwMsg({ kind: 'err', text: 'New password and confirmation do not match.' });
      return;
    }
    setPwBusy(true);
    try {
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      await axios.post(`${API}/auth/change-password`, { current_password: pwCurrent, new_password: pwNew });
      setPwMsg({ kind: 'ok', text: 'Password updated. You can use the new password next time you sign in.' });
      setPwCurrent('');
      setPwNew('');
      setPwConfirm('');
      setTimeout(() => { setPwOpen(false); setPwMsg({ kind: '', text: '' }); }, 2500);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Failed to update password.';
      setPwMsg({ kind: 'err', text: typeof detail === 'string' ? detail : 'Failed to update password.' });
    } finally {
      setPwBusy(false);
    }
  };

  const [showProgramPicker, setShowProgramPicker] = useState(false);
  const [editSection, setEditSection] = useState(null);
  const [nameEditing, setNameEditing] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const openEdit = (section) => setEditSection(section);
  const closeEdit = () => setEditSection(null);

  // Display name resolver — profile.name (what they entered during onboarding)
  // takes priority over the auth user record's name, which may still be the
  // seed default ("Admin") for founder accounts.
  const displayName = profile.name || user?.name || user?.email || 'Athlete';

  const saveName = () => {
    const trimmed = (nameDraft || '').trim();
    if (!trimmed) { setNameEditing(false); return; }
    update({ name: trimmed });
    setNameEditing(false);
  };
  const eqLookup = Object.fromEntries(equipmentCategories.flatMap((c) => c.items).map((i) => [i.id, i.name]));
  const goalLookup = Object.fromEntries(goalOptions.map((g) => [g.id, g.name]));
  const expLookup = Object.fromEntries(experienceLevels.map((e) => [e.id, e.name]));
  const recLookup = Object.fromEntries(recoveryActivities.map((r) => [r.id, r.name]));
  const injLookup = Object.fromEntries([...injuryRegions, ...injurySystemic].map((i) => [i.id, i.name]));
  const activeInjuries = Object.entries(profile.injuries || {}).filter(([, v]) => v).map(([k]) => injLookup[k] || k);
  const currentProgram = programs[profile.programType] || programs['upper-lower'];

  return (
    <div className="bg-transparent text-enf-text-primary min-h-screen pt-20 pb-16">
      <div className="max-w-5xl mx-auto px-6">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="text-enf-accent-fg text-xs uppercase tracking-[0.3em] font-semibold mb-2">Your Profile</div>
            <h1 className="text-4xl md:text-5xl font-black tracking-tighter">{profile.name || 'Athlete'}.</h1>
            <div className="text-enf-text-secondary text-sm mt-1">{profile.age ? `${profile.age} yrs · ` : ''}{profile.heightFt}'{profile.heightIn || 0}" · {profile.weight ? `${displayWeight(profile.weight, profile.units)} ${weightUnit(profile.units)}` : '—'}</div>
          </div>
          {/* Edit profile button removed — every card on this page
              is independently editable; the button was a redundant entry
              into the onboarding flow that trapped users once they had
              completed all 12 steps. */}
        </div>

        {!profile.completedOnboarding && (
          <div
            className="mb-6 rounded-xl border border-enf-accent-fg/40 bg-gradient-to-br from-enf-surface-special/60 via-zinc-950 to-zinc-950 p-6"
            data-testid="resume-onboarding-card"
          >
            <div className="flex flex-col sm:flex-row sm:items-center gap-5">
              <div className="w-12 h-12 rounded-md bg-enf-accent/15 border border-enf-accent-fg/30 flex items-center justify-center shrink-0">
                <ArrowRight className="w-5 h-5 text-enf-accent-fg" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs uppercase tracking-[0.3em] text-enf-role-info font-semibold mb-1">Onboarding in progress</div>
                <h3 className="text-lg font-semibold text-enf-text-primary mb-1">Pick up where you left off.</h3>
                <p className="text-sm text-enf-text-secondary leading-relaxed">
                  Finish the quick setup and your dashboard fills in with a plan built around your equipment, time, and goals. No pressure — come back when it fits.
                </p>
              </div>
              <div className="flex flex-col items-stretch sm:items-end gap-2 shrink-0">
                <Link
                  to="/onboarding"
                  className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-enf-accent hover:bg-enf-accent-hover rounded-md font-semibold text-sm transition-colors whitespace-nowrap"
                  data-testid="resume-onboarding-quickstart"
                >
                  Continue setup <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/onboarding/full"
                  className="text-xs text-enf-text-secondary hover:text-enf-role-info transition-colors text-center sm:text-right"
                  data-testid="resume-onboarding-full"
                >
                  Or take the full setup &rarr;
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Account & cloud sync card — moved to TOP of profile per user request */}
        <div className="mt-6 bg-enf-surface-deep border border-enf-surface-border rounded-xl p-6" data-testid="account-card">
          <div className="flex items-center gap-2 mb-4">
            {user ? <Cloud className="w-4 h-4 text-enf-role-info" /> : <CloudOff className="w-4 h-4 text-enf-text-muted" />}
            <h3 className="text-sm font-semibold uppercase tracking-widest text-enf-text-secondary">Account & cloud sync</h3>
          </div>
          {user ? (
            <>
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                {nameEditing ? (
                  <form
                    onSubmit={(e) => { e.preventDefault(); saveName(); }}
                    className="flex items-center gap-2"
                  >
                    <input
                      autoFocus
                      value={nameDraft}
                      onChange={(e) => setNameDraft(e.target.value)}
                      placeholder="Your name"
                      className="bg-enf-surface-mid border border-enf-surface-border rounded-md px-3 py-1.5 text-base font-semibold text-enf-text-primary focus:outline-none focus:border-enf-accent-fg"
                      data-testid="account-name-input"
                      maxLength={64}
                    />
                    <button
                      type="submit"
                      className="px-3 py-1.5 bg-enf-accent hover:bg-enf-accent-hover rounded-md text-xs font-semibold text-enf-text-primary"
                      data-testid="account-name-save"
                    >Save</button>
                    <button
                      type="button"
                      onClick={() => setNameEditing(false)}
                      className="px-3 py-1.5 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border rounded-md text-xs font-medium text-enf-text-secondary"
                      data-testid="account-name-cancel"
                    >Cancel</button>
                  </form>
                ) : (
                  <button
                    type="button"
                    onClick={() => { setNameDraft(displayName); setNameEditing(true); }}
                    className="group inline-flex items-center gap-2 text-base font-semibold text-enf-text-primary hover:text-enf-accent-fg transition-colors text-left"
                    data-testid="account-name-edit"
                    title="Edit your display name"
                  >
                    <span>{displayName}</span>
                    <Edit3 className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity text-enf-text-muted group-hover:text-enf-accent-fg" />
                  </button>
                )}
                <div className="text-xs text-enf-text-muted mt-0.5">
                  Signed in as <span className="text-enf-role-info">{user.email}</span> · cohort: {user.cohort || 'untagged'}
                </div>
                <div className="text-xs text-enf-text-muted mt-1">Your training data syncs across devices in real time.</div>

                {/* Social handle — shown on share cards as @handle. Optional;
                    private mode is just leaving it blank. Future: contest /
                    recognition opt-in toggle. */}
                <div className="mt-3 pt-3 border-t border-enf-surface-border">
                  <label className="text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold block mb-1.5">Social handle (appears on share cards)</label>
                  <div className="flex items-center gap-2">
                    <span className="text-enf-role-info font-mono text-sm">@</span>
                    <input
                      type="text"
                      value={(profile.socialHandle || '').replace(/^@/, '')}
                      onChange={(e) => update({ socialHandle: e.target.value.replace(/^@/, '').replace(/\s/g, '') })}
                      placeholder="your_handle"
                      data-testid="profile-social-handle"
                      className="flex-1 px-2 py-1 bg-enf-surface-mid border border-enf-surface-border rounded text-sm font-mono text-enf-text-primary focus:outline-none focus:border-enf-role-info"
                    />
                  </div>
                  <div className="text-[11px] text-enf-text-muted mt-1">Used in place of your name on share cards. Leave blank to keep cards anonymous.</div>
                </div>
                {user.email_verified === false && (
                  <div className="mt-3 px-3 py-2.5 bg-amber-950/40 border border-amber-900/60 rounded-md flex items-start gap-2.5" data-testid="verify-email-nudge">
                    <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      {verifyState === 'sent' ? (
                        <div className="text-xs text-amber-100">
                          Verification email sent. Check your inbox.
                        </div>
                      ) : (
                        <>
                          <div className="text-xs text-amber-100">
                            Verify your email to fully secure your account.
                          </div>
                          <button
                            type="button"
                            onClick={sendVerification}
                            disabled={verifyState === 'sending'}
                            className="mt-1.5 text-xs font-semibold text-amber-300 hover:text-amber-100 disabled:text-amber-500/50 disabled:cursor-not-allowed underline-offset-2 hover:underline transition-colors"
                            data-testid="verify-email-send-button"
                          >
                            {verifyState === 'sending' ? 'Sending...' : 'Send verification email'}
                          </button>
                          {verifyState === 'error' && verifyError && (
                            <div className="text-xs text-amber-400/80 mt-1">{verifyError}</div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {user.role === 'admin' && (
                  <Link to="/admin" className="px-4 py-2 bg-enf-accent/10 hover:bg-enf-accent border border-enf-accent-fg/40 hover:border-enf-accent-fg text-enf-accent-fg hover:text-enf-text-primary rounded-md text-sm font-semibold transition-colors flex items-center gap-2" data-testid="account-admin-link">
                    <Shield className="w-4 h-4" /> Admin
                  </Link>
                )}
                <button
                  onClick={() => setPwOpen((v) => !v)}
                  className="px-4 py-2 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border hover:border-enf-surface-border rounded-md text-sm font-medium transition-colors flex items-center gap-2"
                  data-testid="account-change-password"
                >
                  <KeyRound className="w-4 h-4" /> {pwOpen ? 'Cancel' : 'Change password'}
                </button>
                <button
                  onClick={logout}
                  className="px-4 py-2 bg-enf-surface-mid hover:bg-enf-accent border border-enf-surface-border hover:border-enf-accent-fg rounded-md text-sm font-medium transition-colors flex items-center gap-2"
                  data-testid="account-logout"
                >
                  <LogOut className="w-4 h-4" /> Sign out
                </button>
              </div>
            </div>

            {pwOpen && (
              <form onSubmit={submitPasswordChange} className="mt-5 pt-5 border-t border-enf-surface-border space-y-3" data-testid="change-password-form">
                {pwMsg.text && (
                  <div className={`px-3 py-2 rounded-md text-sm ${pwMsg.kind === 'ok' ? 'bg-emerald-900/20 border border-emerald-700/40 text-emerald-300' : 'bg-enf-accent/10 border border-enf-accent-fg/40 text-enf-accent-fg'}`}>
                    {pwMsg.text}
                  </div>
                )}
                <div className="grid sm:grid-cols-3 gap-3">
                  <label className="block">
                    <span className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-1.5 block">Current password</span>
                    <input
                      type={pwShow ? 'text' : 'password'} required autoComplete="current-password"
                      value={pwCurrent} onChange={(e) => setPwCurrent(e.target.value)}
                      className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded-md text-enf-text-primary focus:outline-none focus:border-enf-accent-fg"
                      data-testid="pw-current"
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-1.5 block">New password (8+)</span>
                    <input
                      type={pwShow ? 'text' : 'password'} required minLength={8} autoComplete="new-password"
                      value={pwNew} onChange={(e) => setPwNew(e.target.value)}
                      className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded-md text-enf-text-primary focus:outline-none focus:border-enf-accent-fg"
                      data-testid="pw-new"
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-1.5 block">Confirm new</span>
                    <input
                      type={pwShow ? 'text' : 'password'} required minLength={8} autoComplete="new-password"
                      value={pwConfirm} onChange={(e) => setPwConfirm(e.target.value)}
                      className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded-md text-enf-text-primary focus:outline-none focus:border-enf-accent-fg"
                      data-testid="pw-confirm"
                    />
                  </label>
                </div>
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <button type="button" onClick={() => setPwShow((v) => !v)} className="text-xs text-enf-text-secondary hover:text-enf-text-primary flex items-center gap-1.5">
                    {pwShow ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                    {pwShow ? 'Hide passwords' : 'Show passwords'}
                  </button>
                  <button type="submit" disabled={pwBusy}
                    className="px-4 py-2 bg-enf-accent hover:bg-enf-accent-hover disabled:bg-enf-surface-elevated disabled:text-enf-text-muted rounded-md text-sm font-semibold transition-colors flex items-center gap-2"
                    data-testid="pw-submit"
                  >
                    <KeyRound className="w-4 h-4" /> {pwBusy ? 'Updating...' : 'Update password'}
                  </button>
                </div>
              </form>
            )}
            </>
          ) : (
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <div className="text-base font-semibold text-enf-text-primary">Local-only mode</div>
                <div className="text-xs text-enf-text-muted mt-0.5">Your data lives on this device. Clear your browser, lose your work.</div>
                <div className="text-xs text-enf-text-muted mt-1">Sign in with an invite code to back up everything to the cloud and sync across devices.</div>
              </div>
              <div className="flex gap-2">
                <Link to="/login" className="px-4 py-2 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border rounded-md text-sm font-medium transition-colors flex items-center gap-2" data-testid="account-login">
                  <LogIn className="w-4 h-4" /> Sign in
                </Link>
                <Link to="/register" className="px-4 py-2 bg-enf-accent hover:bg-enf-accent-hover rounded-md text-sm font-semibold transition-colors flex items-center gap-2" data-testid="account-register">
                  Use invite code
                </Link>
              </div>
            </div>
          )}
        </div>

        <SubscriptionBlock />

        {/* Display preferences link — sits directly under account card per user request */}
        <Link
          to="/profile/display"
          className="mt-4 bg-enf-surface-deep hover:bg-enf-surface-mid border border-enf-surface-border hover:border-enf-surface-border rounded-xl p-6 flex items-center justify-between gap-4 transition-colors group"
          data-testid="profile-display-prefs-link"
        >
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-md bg-enf-accent/10 border border-enf-accent-fg/30 flex items-center justify-center flex-shrink-0">
              <SlidersHorizontal className="w-5 h-5 text-enf-accent-fg" />
            </div>
            <div>
              <div className="font-display text-2xl tracking-wide leading-none mb-1">Display preferences</div>
              <p className="text-sm text-enf-text-secondary">Hide cards you don't need. Switch to Simplified Mode anytime. Reset to defaults in one tap.</p>
            </div>
          </div>
          <ArrowRight className="w-5 h-5 text-enf-text-muted group-hover:text-enf-accent-fg transition-colors flex-shrink-0" />
        </Link>

        <ProfileCompletenessMeter />

        {/* G13 — Apparel coupon code, paid-only. ENFERNO10
            = 10% off the merch storefront. Surfaced here because Profile
            is the canonical "your account perks" surface; the same code
            is intentionally NOT shown on the marketing pages so it stays
            a paid-tier reward. Storefront vendor selection still TBD,
            so the CTA is intentionally a static text-and-copy affordance
            until G12 (merch preview page) ships with the live link. */}
        {showApparelCoupon && (
          <div
            className="bg-enf-surface-deep border border-enf-accent-fg/30 rounded-xl p-6 mt-6 flex items-start gap-4"
            data-testid="profile-apparel-coupon-card"
          >
            <div className="w-10 h-10 rounded-md bg-enf-accent/15 border border-enf-accent-fg/30 flex items-center justify-center flex-shrink-0">
              <Shirt className="w-5 h-5 text-enf-accent-fg" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display text-2xl tracking-wide leading-none mb-1">Apparel discount — yours as a member</div>
              <p className="text-sm text-enf-text-secondary mb-3">
                10% off every Enferno apparel order. Code is yours for as long as your Enferno membership is active.
              </p>
              <button
                type="button"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText('ENFERNO10');
                    setCouponCopied(true);
                    setTimeout(() => setCouponCopied(false), 2000);
                  } catch { /* ignore */ }
                }}
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-accent-fg/40 hover:border-enf-accent-fg rounded-md text-sm font-mono font-semibold text-enf-accent-fg transition-colors"
                data-testid="profile-apparel-coupon-copy"
                aria-label="Copy apparel coupon code"
              >
                ENFERNO10
                {couponCopied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                <span className="text-xs text-enf-text-muted font-sans font-normal">{couponCopied ? 'Copied!' : 'Copy'}</span>
              </button>
            </div>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-5 mt-6">
          <Section icon={Flame} iconFlipX title="Experience" onEdit={openEdit} editSection="experience">
            <div className="text-2xl font-black capitalize">{expLookup[profile.experience] || 'Not set'}</div>
          </Section>

          <Section icon={Clock} title="Commitment" onEdit={openEdit} editSection="commitment">
            <div className="flex items-baseline gap-2"><span className="text-2xl font-black">{profile.daysPerWeek}</span><span className="text-enf-text-muted text-sm">days/week</span></div>
            <div className="flex items-baseline gap-2"><span className="text-2xl font-black text-enf-accent-fg">{profile.minutesPerSession}</span><span className="text-enf-text-muted text-sm">min/session</span></div>
            <div className="text-xs text-enf-text-muted mt-1">{profile.restSeconds ?? 90}s rest between sets</div>
          </Section>

          <div className="bg-enf-surface-deep border border-enf-surface-border rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <LayoutGrid className="w-4 h-4 text-enf-accent-fg" />
                <h3 className="text-sm font-semibold uppercase tracking-widest text-enf-text-secondary">Program</h3>
              </div>
              <button onClick={() => setShowProgramPicker(true)} className="text-xs text-enf-text-muted hover:text-enf-accent-fg flex items-center gap-1">
                <Edit3 className="w-3 h-3" /> Change
              </button>
            </div>
            <div className="font-display text-2xl tracking-wide leading-none mb-1">{currentProgram.name}</div>
            <div className="text-xs text-enf-text-muted">{currentProgram.description}</div>
          </div>

          <div className="bg-enf-surface-deep border border-enf-surface-border rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <CalendarDays className="w-4 h-4 text-enf-accent-fg" />
                <h3 className="text-sm font-semibold uppercase tracking-widest text-enf-text-secondary">Day 1 (program start)</h3>
              </div>
              <Popover>
                <PopoverTrigger asChild>
                  <button className="text-xs text-enf-text-muted hover:text-enf-accent-fg flex items-center gap-1">
                    <Edit3 className="w-3 h-3" /> Change
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-enf-surface-deep border-enf-surface-border" align="end">
                  <Calendar
                    mode="single"
                    selected={profile.startDate ? new Date(profile.startDate + 'T00:00:00') : new Date()}
                    onSelect={(d) => d && update({ startDate: localISO(d), schedule: null })}
                    initialFocus
                    className="bg-enf-surface-deep text-enf-text-primary"
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="font-display text-2xl tracking-wide leading-none mb-1">
              {profile.startDate ? format(new Date(profile.startDate + 'T00:00:00'), 'EEE, MMM d, yyyy') : 'Today'}
            </div>
            <div className="text-xs text-enf-text-muted">Anchors your weekly rotation. Editing resets any custom week tweaks.</div>
          </div>

          <Section icon={Target} title="Goals" onEdit={openEdit} editSection="goals">
            <div className="flex flex-wrap gap-2">
              {(profile.goals || []).length ? profile.goals.map((g) => (
                <span key={g} className="px-3 py-1 rounded-full bg-enf-surface-mid border border-enf-surface-border text-sm">{goalLookup[g] || g}</span>
              )) : <span className="text-enf-text-muted text-sm">No goals set</span>}
            </div>
          </Section>

          <Section icon={AlertCircle} title="Limitations" onEdit={openEdit} editSection="limitations">
            <div className="flex flex-wrap gap-2">
              {activeInjuries.length ? activeInjuries.map((i) => (
                <span key={i} className="px-3 py-1 rounded-full bg-enf-accent/10 border border-enf-accent-fg/30 text-enf-accent-fg text-sm">{i}</span>
              )) : <span className="text-enf-text-muted text-sm">None flagged</span>}
            </div>
            {profile.injuryNotes && <div className="text-xs text-enf-text-muted mt-3 italic">“{profile.injuryNotes}”</div>}
          </Section>

          <Section icon={Wrench} title="Equipment" onEdit={openEdit} editSection="equipment">
            <div className="flex flex-wrap gap-2">
              {profile.fullGym ? (
                <span className="px-2.5 py-1 rounded bg-enf-surface-mid text-xs text-enf-text-secondary">Full gym access</span>
              ) : profile.noEquipment ? (
                <span className="px-2.5 py-1 rounded bg-enf-surface-mid text-xs text-enf-text-secondary">Bodyweight / no equipment</span>
              ) : (profile.equipment || []).length ? (
                <>
                  {profile.equipment.slice(0, 10).map((e) => (
                    <span key={e} className="px-2.5 py-1 rounded bg-enf-surface-mid text-xs text-enf-text-secondary">{eqLookup[e] || e}</span>
                  ))}
                  {profile.equipment.length > 10 && <span className="px-2.5 py-1 text-xs text-enf-text-muted">+{profile.equipment.length - 10} more</span>}
                </>
              ) : (
                <span className="text-enf-text-muted text-sm">No equipment selected</span>
              )}
            </div>
          </Section>

          <Section icon={Activity} title="Endurance activities" onEdit={openEdit} editSection="recovery">
            <div className="flex flex-wrap gap-2">
              {(profile.recoveryActivities || []).length ? profile.recoveryActivities.map((r) => (
                <Link key={r} to="/endurance" className="px-3 py-1 rounded-full bg-enf-surface-mid border border-enf-surface-border hover:border-enf-accent-fg/40 text-sm flex items-center gap-1">
                  {recLookup[r] || r} <ChevronRight className="w-3 h-3" />
                </Link>
              )) : <span className="text-enf-text-muted text-sm">None selected</span>}
            </div>
          </Section>
        </div>

        <div className="mt-8 bg-enf-surface-deep border border-enf-surface-border rounded-xl p-6" data-testid="adaptive-mode-card">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              <ShieldCheck className={`w-5 h-5 mt-0.5 ${profile.adaptiveMode ? 'text-adaptive-accent' : 'text-enf-text-muted'}`} />
              <div>
                <div className="text-sm font-semibold uppercase tracking-widest text-enf-text-secondary">Adaptive Mode</div>
                <div className="text-base mt-1">
                  {profile.adaptiveMode ? (
                    <span className="text-adaptive-accent font-semibold">Active</span>
                  ) : (
                    <span className="text-enf-text-secondary">Off — using Athlete Mode</span>
                  )}
                </div>
                <p className="text-xs text-enf-text-muted mt-1 max-w-md">
                  Pacing-aware, flare-aware, anti-shame coaching for chronic pain and amputee training. Perimenopause, older-adult, and post-surgical support in development — coming soon.
                </p>
                {profile.adaptiveMode && (profile.adaptiveSubmodes || []).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {profile.adaptiveSubmodes.map((sm) => (
                      <span key={sm} className="px-2 py-0.5 rounded-full bg-adaptive-accent/15 border border-adaptive-accent/30 text-adaptive-accent text-xs">
                        {sm.replace('-', ' ')}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={() => update({ adaptiveMode: !profile.adaptiveMode })}
              data-testid="adaptive-mode-toggle"
              className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors ${
                profile.adaptiveMode
                  ? 'bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border text-enf-text-secondary'
                  : 'bg-adaptive-accent hover:bg-adaptive-accent-hover text-enf-text-primary'
              }`}
            >
              {profile.adaptiveMode ? 'Switch to Athlete Mode' : 'Enable Adaptive Mode'}
            </button>
          </div>

          {/* Submode picker — visible whenever Adaptive Mode is on */}
          {profile.adaptiveMode && (
            <div className="mt-6 pt-6 border-t border-enf-surface-border" data-testid="adaptive-submodes-section">
              <div className="text-sm font-semibold uppercase tracking-widest text-enf-text-secondary mb-1">Which best describes you?</div>
              <p className="text-xs text-enf-text-muted mb-4 max-w-md">
                Multi-select. Tap any that apply. Adaptive programming branches per submode — the more accurate this is, the more relevant your workouts and content.
              </p>
              <div className="flex flex-col gap-2">
                {ADAPTIVE_SUBMODES.map((sm) => {
                  const selected = (profile.adaptiveSubmodes || []).includes(sm.id);
                  return (
                    <button
                      key={sm.id}
                      type="button"
                      onClick={() => {
                        const current = profile.adaptiveSubmodes || [];
                        const next = selected
                          ? current.filter((id) => id !== sm.id)
                          : [...current, sm.id];
                        update({ adaptiveSubmodes: next });
                      }}
                      data-testid={`adaptive-submode-${sm.id}`}
                      className={`text-left p-3 rounded-lg border transition-colors ${
                        selected
                          ? 'bg-adaptive-accent/15 border-adaptive-accent/40'
                          : 'bg-enf-surface-mid border-enf-surface-border hover:border-enf-surface-border'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {selected && <Check className="w-4 h-4 text-adaptive-accent" />}
                        <span className={`text-sm font-semibold ${selected ? 'text-adaptive-accent' : 'text-enf-text-primary'}`}>{sm.label}</span>
                      </div>
                      <div className="text-xs text-enf-text-muted mt-1">{sm.blurb}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Prosthesis variants picker — visible only for Adaptive amputee users */}
          {profile.adaptiveMode && (profile.adaptiveSubmodes || []).includes('amputee') && (
            <div className="mt-6 pt-6 border-t border-enf-surface-border" data-testid="prosthesis-variants-section">
              <div className="text-sm font-semibold uppercase tracking-widest text-enf-text-secondary mb-1">Prosthesis variants</div>
              <p className="text-xs text-enf-text-muted mb-4 max-w-md">
                Multi-select. Pick every variant that applies to you — day-prosthesis, sport blade, and any mobility aids you use. Adaptive programming uses this to surface the most relevant exercises.
              </p>
              <div className="flex flex-wrap gap-2">
                {PROSTHESIS_VARIANTS.map((v) => {
                  const selected = (profile.prosthesisVariants || []).includes(v.id);
                  return (
                    <button
                      key={v.id}
                      type="button"
                      onClick={() => {
                        const current = profile.prosthesisVariants || [];
                        const next = selected
                          ? current.filter((id) => id !== v.id)
                          : [...current, v.id];
                        update({ prosthesisVariants: next });
                      }}
                      data-testid={`prosthesis-variant-${v.id}`}
                      className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                        selected
                          ? 'bg-adaptive-accent/20 border-adaptive-accent/40 text-adaptive-accent'
                          : 'bg-enf-surface-mid border-enf-surface-border text-enf-text-secondary hover:border-enf-surface-border hover:text-enf-text-primary'
                      }`}
                    >
                      {selected && <Check className="w-3 h-3 inline mr-1" />}
                      {v.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Residual-limb daily log — IronLimb Session B. Visible only when
            adaptive amputee submode is active. Anchor id used by Library's
            Adaptive Lens banner deeplink. */}
        {profile.adaptiveMode && (profile.adaptiveSubmodes || []).includes('amputee') && (
          <div id="residual-limb">
            <ResidualLimbLogCard />
          </div>
        )}

        {/* Adaptive Learn — links to in-app educational content. Visible
            whenever Adaptive Mode is on, regardless of submode. */}
        {profile.adaptiveMode && (
          <div className="bg-enf-surface-deep border border-enf-surface-border rounded-xl p-6" data-testid="adaptive-learn-section">
            <div className="text-sm font-semibold uppercase tracking-widest text-enf-text-secondary mb-3 flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-adaptive-accent" /> Learn
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {phantomLimbEnabled && (
              <Link
                to="/adaptive/phantom-limb"
                data-testid="learn-phantom-limb"
                className="bg-enf-surface-mid border border-enf-surface-border rounded-lg p-4 hover:border-adaptive-accent/40 transition-colors group"
              >
                <div className="text-[10px] uppercase tracking-widest text-adaptive-accent font-bold mb-1">Awareness</div>
                <div className="text-sm font-semibold text-enf-text-primary mb-1">Phantom-limb awareness</div>
                <div className="text-xs text-enf-text-secondary">What sensations are, evidence-based management, when to talk to your clinician.</div>
              </Link>
              )}
              <Link
                to="/library?lens=adaptive"
                data-testid="learn-library-lens"
                className="bg-enf-surface-mid border border-enf-surface-border rounded-lg p-4 hover:border-adaptive-accent/40 transition-colors group"
              >
                <div className="text-[10px] uppercase tracking-widest text-adaptive-accent font-bold mb-1">Library</div>
                <div className="text-sm font-semibold text-enf-text-primary mb-1">Open Library with Adaptive lens</div>
                <div className="text-xs text-enf-text-secondary">Exercises, mobility / rehab, adaptive gear — filtered to what's relevant for you.</div>
              </Link>
            </div>
          </div>
        )}

        {/* Display preferences link + Account card moved to top of page per user request */}

        {/* BJJ journal export was here — removed and relocated
            to the per-art surface on /training. Per-art card sits where the
            user already is; the consolidated Profile card was redundant. */}

        <div className="mt-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-5 border border-enf-surface-border rounded-xl bg-enf-surface-deep" data-testid="reset-history-card">
          <div>
            <div className="text-sm font-semibold">Reset training history</div>
            <div className="text-xs text-enf-text-muted max-w-md">
              Wipes every logged workout and martial-arts session ({history.length + (training?.sessions?.length || 0)} entries) from your account.
              Leaves your profile, preferences, equipment, streaks, and pain log intact.
              Use this when beta testing has filled your analytics with throwaway data.
            </div>
          </div>
          <button
            onClick={() => setResetHistoryState({ open: true, busy: false, result: null })}
            className="px-4 py-2 bg-enf-surface-mid hover:bg-enf-role-danger/20 border border-enf-surface-border hover:border-enf-role-danger rounded-md text-sm font-medium text-enf-text-primary hover:text-enf-role-danger transition-colors whitespace-nowrap"
            data-testid="reset-history-button"
          >
            Reset history
          </button>
        </div>

        <div className="mt-5 flex items-center justify-between p-5 border border-enf-surface-border rounded-xl bg-enf-surface-deep">
          <div>
            <div className="text-sm font-semibold">Start over</div>
            <div className="text-xs text-enf-text-muted">Reset your profile and run onboarding again.</div>
          </div>
          <button onClick={() => { if (window.confirm('Reset your Enferno profile? This clears all stored preferences.')) reset(); }}
            className="px-4 py-2 bg-enf-surface-mid hover:bg-enf-accent border border-enf-surface-border hover:border-enf-accent-fg rounded-md text-sm font-medium transition-colors">
            Reset profile
          </button>
        </div>

        {/* Privacy & data controls — GDPR Articles 15 & 17 (P0-OPS4) */}
        <div className="mt-10 pt-6 border-t border-enf-surface-border">
          <div className="text-xs uppercase tracking-[0.3em] font-semibold text-enf-text-muted mb-4">Privacy & data</div>

          {/* Consent Center — granular toggles for aggregate insights, AI training, peer matching, etc. */}
          <ConsentCenter />

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-5 border border-enf-surface-border rounded-xl bg-enf-surface-deep mt-5" data-testid="export-data-card">
            <div>
              <div className="text-sm font-semibold">Export my data</div>
              <div className="text-xs text-enf-text-muted max-w-md">
                Download a ZIP with every record we hold about you — workouts, sessions, profile, residual-limb logs, donations.
                Photos, video, and raw GPS points are device-local and not included. We'll email you a download link (valid 24h).
              </div>
            </div>
            <button
              onClick={async () => {
                if (exportBusy) return;
                setExportBusy(true);
                try {
                  const r = await axios.post(`${API}/account/export`);
                  alert(r.data?.message || 'Export started. Check your email shortly.');
                } catch (err) {
                  alert(err?.response?.data?.detail || 'Failed to start export. Try again in a minute.');
                } finally {
                  setExportBusy(false);
                }
              }}
              disabled={exportBusy}
              data-testid="export-data-button"
              className="px-4 py-2 bg-enf-surface-mid hover:bg-enf-role-info/20 border border-enf-surface-border hover:border-enf-role-info rounded-md text-sm font-medium text-enf-text-primary hover:text-enf-role-info transition-colors whitespace-nowrap disabled:opacity-50"
            >
              {exportBusy ? 'Starting…' : 'Email me my data'}
            </button>
          </div>

          <div className="mt-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-5 border border-enf-role-danger/30 rounded-xl bg-enf-surface-deep" data-testid="delete-account-card">
            <div>
              <div className="text-sm font-semibold text-enf-role-danger">Delete my account</div>
              <div className="text-xs text-enf-text-muted max-w-md">
                Permanently deletes your account and every record we hold. We'll email you a confirmation with a 7-day undo link.
                Active subscriptions will be set to cancel at the end of the current billing period.
                After 7 days, deletion is irreversible.
              </div>
            </div>
            <button
              onClick={async () => {
                if (deleteBusy) return;
                const phrase = window.prompt('Type DELETE to permanently queue your account for deletion. You will have 7 days to undo via the email we send you.');
                if ((phrase || '').trim().toUpperCase() !== 'DELETE') return;
                setDeleteBusy(true);
                try {
                  const r = await axios.post(`${API}/account/delete`);
                  alert(r.data?.message || 'Account queued for deletion. Check your email.');
                  window.location.href = '/';
                } catch (err) {
                  alert(err?.response?.data?.detail || 'Failed to queue deletion. Try again or contact privacy@enferno.app.');
                } finally {
                  setDeleteBusy(false);
                }
              }}
              disabled={deleteBusy}
              data-testid="delete-account-button"
              className="px-4 py-2 bg-enf-surface-mid hover:bg-enf-role-danger/20 border border-enf-role-danger/40 hover:border-enf-role-danger rounded-md text-sm font-semibold text-enf-role-danger hover:text-enf-role-danger transition-colors whitespace-nowrap disabled:opacity-50"
            >
              {deleteBusy ? 'Queuing…' : 'Delete account'}
            </button>
          </div>
        </div>

        <div className="mb-6">
          <HealthPermissionCard />
        </div>
      </div>

      {/* Program picker */}
      <Dialog open={showProgramPicker} onOpenChange={setShowProgramPicker}>
        <DialogContent className="bg-enf-surface-deep border-enf-surface-border text-enf-text-primary max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl tracking-wide leading-none">Change your program</DialogTitle>
            <DialogDescription className="text-enf-text-secondary">
              Switching regenerates your weekly schedule using your {profile.daysPerWeek || 4} training days.
            </DialogDescription>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3 mt-2">
            {Object.values(programs)
              .map((p) => {
                const active = (profile.programType || 'upper-lower') === p.id;
                const fits = (profile.daysPerWeek || 4) >= p.minDays;
                return (
                  <button key={p.id} onClick={() => {
                    update({ programType: p.id, schedule: null });
                    setShowProgramPicker(false);
                  }}
                    className={`text-left p-4 rounded-xl border transition-all ${
                      active ? 'bg-enf-accent/10 border-enf-accent-fg' : 'bg-enf-surface-mid border-enf-surface-border hover:border-enf-surface-border'
                    }`}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="font-display text-2xl tracking-wide">{p.name}</div>
                      {active && <Check className="w-4 h-4 text-enf-accent-fg" />}
                    </div>
                    <div className="text-sm text-enf-text-secondary mb-2">{p.description}</div>
                    <div className="text-xs text-enf-text-muted">
                      Best for: {p.bestFor}
                      {!fits && <span className="ml-2 text-amber-400">· needs {p.minDays}+ days/wk</span>}
                    </div>
                  </button>
                );
              })}
          </div>
        </DialogContent>
      </Dialog>

      <EditProfileModal section={editSection} open={!!editSection} onClose={closeEdit} />

      {/* Reset training history — confirm dialog */}
      <Dialog
        open={resetHistoryState.open}
        onOpenChange={(o) => !o && setResetHistoryState({ open: false, busy: false, result: null })}
      >
        <DialogContent className="bg-enf-surface-deep border-enf-surface-border text-enf-text-primary max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl tracking-wide leading-none flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-enf-role-warning" /> Reset training history?
            </DialogTitle>
            <DialogDescription className="text-enf-text-secondary leading-relaxed mt-2">
              This permanently deletes every logged workout and martial-arts session for your account
              <strong className="text-enf-text-primary"> ({history.length + (training?.sessions?.length || 0)} entries)</strong>.
              Your profile, preferences, equipment, streaks, and pain log are untouched.
              <span className="block mt-2 text-enf-role-warning text-sm">This cannot be undone.</span>
            </DialogDescription>
          </DialogHeader>
          {resetHistoryState.result ? (
            <div className="space-y-3 pt-2">
              {resetHistoryState.result.ok === false ? (
                <div className="text-sm text-enf-role-danger" data-testid="reset-history-result-error">
                  Reset failed: {resetHistoryState.result.error || 'unknown error'}. Local history was still cleared.
                </div>
              ) : (
                <div className="text-sm text-enf-text-secondary" data-testid="reset-history-result-ok">
                  Done. Removed <strong className="text-enf-text-primary">{resetHistoryState.result.workouts_deleted || 0}</strong> workouts
                  and <strong className="text-enf-text-primary">{resetHistoryState.result.training_sessions_deleted || 0}</strong> martial-arts sessions.
                </div>
              )}
              <button
                onClick={() => setResetHistoryState({ open: false, busy: false, result: null })}
                className="w-full px-4 py-2 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border rounded-md text-sm font-medium transition-colors"
                data-testid="reset-history-close"
              >
                Close
              </button>
            </div>
          ) : (
            <div className="flex gap-2 pt-2">
              <button
                onClick={() => setResetHistoryState({ open: false, busy: false, result: null })}
                disabled={resetHistoryState.busy}
                className="flex-1 px-4 py-2 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border rounded-md text-sm font-medium transition-colors disabled:opacity-50"
                data-testid="reset-history-cancel"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  setResetHistoryState((s) => ({ ...s, busy: true }));
                  const result = await resetTrainingHistory();
                  setResetHistoryState({ open: true, busy: false, result });
                }}
                disabled={resetHistoryState.busy}
                className="flex-1 px-4 py-2 bg-enf-role-danger text-white rounded-md text-sm font-semibold hover:bg-red-600 transition-colors disabled:opacity-50"
                data-testid="reset-history-confirm"
              >
                {resetHistoryState.busy ? 'Resetting…' : 'Yes, wipe history'}
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Profile;
